import type { GigRequestStatus } from "./gigTypes";
import type { MediaAssetResponse } from "./mediaTypes";

export type UUID = string;

// Mirrors backend: com.myteengig.backend.dto.domain.SignupRole
export type SignupRole = "TEEN" | "ADULT";
export type AccountType = "PERSONAL" | "BUSINESS";
export type BusinessVolunteerApprovalStatus = "NOT_REQUESTED" | "PENDING" | "APPROVED" | "REJECTED";

// Mirrors backend: com.myteengig.backend.dto.auth.LoginRequest
export type LoginRequestDto = {
  email: string;
  password: string;
};

// Frontend signup payload.
export type SignupRequestDto = {
  email: string;
  password: string;
  phoneNumber?: string | null;
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  zip: string;
  role: SignupRole;
  accountType?: AccountType | null;
  dateOfBirth: string; //`dateOfBirth` is an ISO date string (YYYY-MM-DD).
  bio?: string | null;
  skills?: string[];
  businessName?: string | null;
  businessDescription?: string | null;
  businessWebsite?: string | null;
  businessVolunteerApprovalRequested?: boolean;
  postJobIntent?: boolean;
  termsAccepted: boolean;
};

// Mirrors backend: com.myteengig.backend.dto.auth.AuthUserResponse
export type AuthUserResponseDto = {
  id: UUID;
  email: string;
  pendingEmail: string | null;
  phoneNumber: string | null;
  pendingPhoneNumber: string | null;
  phoneVerified: boolean;
  identityVerified: boolean;
  identityVerificationStatus: "NOT_STARTED" | "PENDING" | "VERIFIED" | "REQUIRES_INPUT";
  identityVerifiedAt: string | null;
  firstName: string;
  lastName: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  accountType?: AccountType | null;
  businessName?: string | null;
  businessDescription?: string | null;
  businessWebsite?: string | null;
  businessVolunteerApprovalStatus?: BusinessVolunteerApprovalStatus | null;
  dateOfBirth: string | null;
  bio: string | null;
  skills: string[];
  profilePhoto: MediaAssetResponse | null;
  status: string;
  roles: string[];
};

export type AuthStatus = "idle" | "loading" | "authenticated" | "anonymous";

export type UseAuthResult = {
  user: AuthUserResponseDto | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  initialize: () => Promise<void>;
  login: (payload: LoginRequestDto) => Promise<AuthUserResponseDto>;
  signup: (payload: SignupRequestDto) => Promise<AuthUserResponseDto>;
  logout: () => Promise<void>;
};

export type CsrfResponseDto = {
  token: string;
};

export type VerifyEmailRequestDto = {
  token: string;
};

export type ResendEmailVerificationRequestDto = {
  email: string;
  postJobIntent?: boolean;
};

export type VerifyEmailResponseDto = {
  mode: string;
  email: string;
};

export type RequestPasswordResetRequestDto = {
  email: string;
};

export type ResetPasswordRequestDto = {
  token: string;
  newPassword: string;
};

export type UpdateAccountProfileRequestDto = {
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  zip: string;
  businessName: string | null;
  businessDescription: string | null;
  businessWebsite: string | null;
  bio: string | null;
  skills: string[];
};

export type UpdatePhoneNumberRequestDto = {
  phoneNumber: string;
};

export type RequestEmailChangeRequestDto = {
  email: string;
};

export type VerifyPhoneNumberRequestDto = {
  code: string;
};

export type DeleteAccountRequestDto = {
  password: string;
  confirmationText: string;
  reason?: string;
};

export type UserNotificationSeverityDto = "INFO" | "ACTION";

export type UserNotificationTypeDto =
  | "INSTALL_APP_REMINDER"
  | "TEEN_PROFILE_DESCRIPTION_MISSING"
  | "TEEN_PROFILE_SKILLS_MISSING"
  | "TEEN_PAYOUT_SETUP_REQUIRED"
  | "TEEN_PAYOUT_SETUP_REQUIRED_FOR_GIG"
  | "TEEN_PAYOUT_READY_WAITING_FOR_POSTER_FUNDING"
  | "GIG_REQUEST_NEEDS_REVIEW"
  | "GUARDIAN_GIG_REQUEST_NEEDS_APPROVAL"
  | "GUARDIAN_TEEN_PAYOUT_SETUP_REQUIRED_FOR_GIG"
  | "GUARDIAN_GIG_PAYOUT_SETUP_NO_LONGER_NEEDED"
  | "GIG_REQUEST_UPDATE_ACKNOWLEDGMENT_REQUIRED"
  | "GIG_REQUEST_RESCHEDULE_REQUIRED"
  | "SCHEDULE_PROPOSAL_RECEIVED"
  | "SCHEDULE_PROPOSAL_CONFIRMED"
  | "GIG_SELECTED"
  | "GIG_SELECTED_CREW_READY"
  | "GIG_REMAINING_CREW_CONFIRMED"
  | "GIG_FILLED_BY_OTHER_TEEN"
  | "POSTER_TEEN_SELECTED_PENDING_PAYOUT_SETUP"
  | "POSTER_SELECTED_TEEN_PAYOUT_READY"
  | "GIG_REQUEST_REJECTED"
  | "GIG_COMPLETION_MARKED"
  | "GIG_COMPLETED"
  | "GIG_COMPLETION_REMINDER"
  | "GIG_CANCELLED"
  | "GIG_EXPIRED_REFUNDED"
  | "TEEN_REMOVED_FROM_GIG"
  | "GIG_ISSUE_OPENED"
  | "GIG_ISSUE_RESOLVED"
  | "GIG_REQUEST_MESSAGE_RECEIVED"
  | "BUSINESS_LISTING_APPLICATION_MESSAGE_RECEIVED"
  | "REVIEW_RECEIVED"
  | "GUARDIAN_TEEN_SELECTED"
  | "GUARDIAN_REQUEST_RECEIVED"
  | "GUARDIAN_CONNECTION_ACTIVE";

export type PageMetadataWebDto = {
  number: number;
  size: number;
  numberOfElements: number | null;
  hasNext: boolean;
  hasPrevious: boolean;
  first: boolean;
  last: boolean;
  sortBy: Array<{
    direction: "ASC" | "DESC";
    propertyName: string;
  }>;
};

export type UserNotificationViewDto = "needs-action" | "recent-updates";

export type UserNotificationResponseDto = {
  id: UUID;
  type: UserNotificationTypeDto;
  severity: UserNotificationSeverityDto;
  entityType: string;
  entityId: UUID;
  title: string;
  body: string;
  ctaPath: string;
  ctaLabel: string;
  createdAt: string;
  readAt: string | null;
  resolvedAt: string | null;
  needsAction: boolean;
};

export type UserNotificationListResponseDto = {
  notifications: UserNotificationResponseDto[];
  page: PageMetadataWebDto;
};

export type UserNotificationSummaryResponseDto = {
  unreadCount: number;
  needsActionCount: number;
  recentUpdatesCount: number;
  unreadNeedsActionCount: number;
  unreadRecentUpdatesCount: number;
};

export type UsZipLocationResponseDto = {
  city: string | null;
  state: string | null;
  zip: string;
  timeZone?: string | null;
};

export type FeedbackTypeDto =
  | "QUESTION"
  | "BUG"
  | "FEATURE_IDEA"
  | "GENERAL_FEEDBACK"
  | "POST_COMPLETION_WEBSITE_FEEDBACK";

export type FeedbackSubmissionRequestDto = {
  type: FeedbackTypeDto;
  email: string;
  message: string;
  sourcePath?: string | null;
  gigRequestId?: string | null;
  respondentRole?: "TEEN" | "POSTER" | null;
  rating?: number | null;
};

export const gigRequestStatusLabel: Record<GigRequestStatus, string> = {
  GUARDIAN_PENDING: "Awaiting Parent/Guardian Gig Approval",
  GUARDIAN_REJECTED: "Parent/Guardian Rejected",
  POSTER_PENDING: "Awaiting Gig Poster Approval",
  POSTER_SELECTED_PENDING_CREW: "Selected, Waiting for Full Crew",
  PAYOUT_SETUP_PENDING: "Selected Pending Payout Setup",
  FUNDING_PENDING: "Awaiting Poster Payment",
  POSTER_ACCEPTED: "Gig Accepted",
  FILLED_BY_OTHER_TEEN: "Filled By Another Teen",
  POSTER_REJECTED: "Gig Poster Rejected",
  EXPIRED: "Gig Expired",
  WITHDRAWN: "Withdrawn",
  REMOVED_BY_POSTER: "Removed by Poster",
  REMOVED_BY_TEEN: "Left Gig",
  CANCELLED: "Cancelled",
};
